import { QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

/**
 * React Query Client Configuration
 *
 * Best Practices:
 * - staleTime: How long data is considered fresh (5 minutes)
 * - gcTime: How long unused data stays in cache (10 minutes)
 * - refetchOnWindowFocus: Refetch when user returns to tab
 * - retry: Number of retry attempts on failure
 *
 * Error logging:
 * - Global mutation onError captures all data-layer mutation failures
 *   to Sentry. `variables` is NOT forwarded — it may contain PHI
 *   (patient IDs, names, medical notes). Only the mutationKey is tagged
 *   as metadata. PHI scrubbing in `main.tsx` beforeSend adds a second
 *   safety net for the `extra` field.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh
      gcTime: 1000 * 60 * 10, // 10 minutes - garbage collection time
      refetchOnWindowFocus: false, // Opt-in per query for real-time domains
      refetchOnReconnect: true, // Refetch on reconnect
      retry: 2, // Retry failed requests 2 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: 1, // Retry mutations once
      onError: (error) => {
        Sentry.captureException(error, {
          extra: {
            source: 'react-query-mutation',
          },
        });
      },
    },
  },
});
